/** Opaque invalidations only: business records remain behind the REST API. */
export type RealtimeStatus = "connecting" | "live" | "degraded" | "unconfigured";

export type RevisionEvent = {
  topics: readonly string[];
  catchUp: boolean;
};
export type RevisionSnapshot = {
  data: Record<string, unknown> | undefined;
  fromCache: boolean;
  hasPendingWrites: boolean;
};
export type RevisionSource = {
  path: string;
  topics: readonly string[];
};
export type RevisionListener = (
  path: string,
  next: (snapshot: RevisionSnapshot) => void,
  error: () => void,
) => () => void;

/**
 * One authoritative baseline per document, then changed revisions only. An
 * offline/cache snapshot never asserts freshness; its next server snapshot
 * catches up even when a document was deleted or no revision changed. No timer
 * or HTTP fallback. Cleanup also fences already queued SDK callbacks.
 */
export function subscribeRevisions({
  sources, listen, isCurrent, onChange, onError, onStatus,
}: {
  sources: readonly RevisionSource[];
  listen: RevisionListener;
  isCurrent: () => boolean;
  onChange: (event: RevisionEvent) => void;
  onError?: () => void;
  onStatus?: (status: RealtimeStatus) => void;
}): () => void {
  let active = true;
  let scheduled = false;
  let catchUp = false;
  const pending = new Set<string>();
  const ready = new Set<string>();
  onStatus?.("connecting");
  const current = () => active && isCurrent();
  const enqueue = (topics: readonly string[], reconnect: boolean) => {
    topics.forEach((topic) => pending.add(topic));
    catchUp ||= reconnect;
    if (scheduled || (!catchUp && pending.size === 0)) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      if (!current() || ready.size !== sources.length) return;
      const event = { topics: [...pending], catchUp };
      pending.clear();
      catchUp = false;
      onChange(event);
    });
  };
  const unsubscriptions = sources.map(({ path, topics }) => {
    let baseline: Record<string, unknown> | undefined;
    let needsCatchUp = true;
    return listen(path, (snapshot) => {
      if (!current()) return;
      if (snapshot.fromCache || snapshot.hasPendingWrites) {
        if (ready.delete(path)) onStatus?.("degraded");
        needsCatchUp = true;
        return;
      }
      ready.add(path);
      if (ready.size === sources.length) onStatus?.("live");
      const data = snapshot.data ?? {};
      const changed = topics.filter((topic) => data[topic] !== baseline?.[topic]);
      enqueue(needsCatchUp ? topics : changed, needsCatchUp);
      baseline = data;
      needsCatchUp = false;
    }, () => {
      if (current()) { onStatus?.("degraded"); onError?.(); }
    });
  });
  return () => {
    active = false;
    pending.clear();
    unsubscriptions.forEach((unsubscribe) => unsubscribe());
  };
}

/** Event fanout for mounted consumers such as the print coordinator. */
export function createRevisionBus<T>() {
  const listeners = new Set<(event: T) => void>();
  return {
    subscribe(listener: (event: T) => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    emit(event: T) { for (const listener of listeners) listener(event); },
  };
}

/**
 * Events, not a timer, request work. Changes arriving during a slow read merge
 * into one follow-up so a busy store cannot endlessly cancel/restart that read.
 */
export function createRevisionRefresh({ isCurrent, refresh, onIdle }: {
  isCurrent: () => boolean;
  refresh: (event: RevisionEvent) => Promise<void>;
  onIdle?: () => void;
}) {
  let active = true;
  let running = false;
  let scheduled = false;
  let catchUp = false;
  const topics = new Set<string>();
  const drain = () => {
    if (!active || !isCurrent() || running || scheduled || (!topics.size && !catchUp)) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      if (!active || !isCurrent() || running) return;
      const event = { topics: [...topics], catchUp };
      topics.clear(); catchUp = false; running = true;
      const settled = () => {
        running = false;
        if (active && isCurrent() && (topics.size || catchUp)) drain();
        else { topics.clear(); catchUp = false; onIdle?.(); }
      };
      // A failed refresh only drains changes which arrived meanwhile; it does
      // not manufacture another event or retry itself.
      void Promise.resolve().then(() => {
        if (active && isCurrent()) return refresh(event);
      }).then(settled, settled);
    });
  };
  return {
    enqueue(event: RevisionEvent) {
      if (!active || !isCurrent()) return;
      event.topics.forEach((topic) => topics.add(topic));
      catchUp ||= event.catchUp;
      drain();
    },
    dispose() { active = false; topics.clear(); },
  };
}

/** Independent resources must not wait behind an unrelated slow request. */
export function createKeyedRevisionRefresh<Key>({ isCurrent, refresh }: {
  isCurrent: () => boolean;
  refresh: (key: Key, event: RevisionEvent) => Promise<void>;
}) {
  const queues = new Map<Key, ReturnType<typeof createRevisionRefresh>>();
  let active = true;
  return {
    enqueue(key: Key, event: RevisionEvent) {
      if (!active || !isCurrent()) return;
      let queue = queues.get(key);
      if (!queue) {
        queue = createRevisionRefresh({
          isCurrent, refresh: (change) => refresh(key, change),
          onIdle: () => { if (queues.get(key) === queue) { queues.delete(key); queue?.dispose(); } },
        });
        queues.set(key, queue);
      }
      queue.enqueue(event);
    },
    get size() { return queues.size; },
    dispose() { active = false; queues.forEach((queue) => queue.dispose()); queues.clear(); },
  };
}
